import type { UnitInfoKwataniScreenInterface } from 'store/units/types';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import type { UnitConfigParams } from 'unitData/BaseUnitConfig';
import { ScreeningMediaDataNormalCalcKL } from 'unitData/LinearMotionScreen/LinearMotionScreenData';
import type { ScreeningMediaInterface } from './KwataniScreenTypes';
import type { KwataniScreenUnitInfoDataMapInterface } from './unitInfoData';

export const TypeOfScreenings: string[] = ['Dry', 'Wet'];

export const nbrOfDecksOptions: number[] = [1, 2];

const defaultDeckDisplay = {
  separationSize: true,
  shape: false,
  model: false,
  openArea: false,
  particleShape: false,
  efficiency: false,
  description: false,
  material: false,
  type: false,
  function: false,
  maxFeedSize: false,
};

export const getDefaultData = (
  unitInfoData: KwataniScreenUnitInfoDataMapInterface,
): UnitInfoKwataniScreenInterface => {
  if (!unitInfoData.models || !unitInfoData.configurations) {
    throw new Error('expected models and configurations to be set');
  }
  const defaultScreeningMedia: ScreeningMediaInterface =
    ScreeningMediaDataNormalCalcKL[3];

  return {
    modelName: Object.keys(unitInfoData.models)[1],
    width: Object.values(unitInfoData.models)[1].width,
    length: Object.values(unitInfoData.models)[1].length,
    weight: Object.values(unitInfoData.models)[1].weight,
    motor: Object.values(unitInfoData.models)[1].motor,
    nbrOfDecks: 2,
    deckList: {
      0: {
        separationSize: 25,
        screeningElement: {
          name: Object.values(unitInfoData.configurations.screeningMediaIds)[0]
            .name,
          value: Object.values(unitInfoData.configurations.screeningMediaIds)[0]
            .value,
        },
        screeningMedia: defaultScreeningMedia,
        openArea: 30,
        particleShape: {
          name: Object.values(unitInfoData.configurations.particleShapes)[0]
            .name,
          value: Object.values(unitInfoData.configurations.particleShapes)[0]
            .value,
        },
        efficiency: {
          name: Object.values(unitInfoData.configurations.efficiencyParams)[1]
            .name,
          value: Object.values(unitInfoData.configurations.efficiencyParams)[1]
            .value,
        },
        efficiencyNumber: 80,
        display: defaultDeckDisplay,
      },
      1: {
        separationSize: 16,
        screeningElement: {
          name: Object.values(unitInfoData.configurations.screeningMediaIds)[0]
            .name,
          value: Object.values(unitInfoData.configurations.screeningMediaIds)[0]
            .value,
        },
        screeningMedia: defaultScreeningMedia,
        openArea: 30,
        particleShape: {
          name: Object.values(unitInfoData.configurations.particleShapes)[0]
            .name,
          value: Object.values(unitInfoData.configurations.particleShapes)[0]
            .value,
        },
        efficiency: {
          name: Object.values(unitInfoData.configurations.efficiencyParams)[1]
            .name,
          value: Object.values(unitInfoData.configurations.efficiencyParams)[1]
            .value,
        },
        efficiencyNumber: 80,
        display: defaultDeckDisplay,
      },
    },
    scalpingScreen: false,
    wetScreening: false,
    materialType: {
      name: Object.values(unitInfoData.configurations.rockMaterials)[0].name,
      value: Object.values(unitInfoData.configurations.rockMaterials)[0].value,
    },
    moisture: 0,
    newMoisture: false,
    displayLoad: true,
    parallelUnits: 1,
    display: {
      displayNotes: {
        noteOne: false,
        noteTwo: false,
        noteThree: false,
        noteFour: false,
        noteFive: false,
      },
      displayUnitName: true,
      displayModelName: true,
      displayLoad: true,
      displayNbrOfDecks: false,
      displayWetScreening: false,
    },
  };
};

export class KwataniScreenConfig extends BaseUnitConfig {
  static getDefaultData(
    params?: UnitConfigParams,
  ): UnitInfoKwataniScreenInterface {
    const unitInfoData = params?.data as
      KwataniScreenUnitInfoDataMapInterface | undefined;

    if (!unitInfoData?.models || !unitInfoData?.configurations) {
      throw new Error('expected models and configurations to be set');
    }
    const defaultScreeningMedia: ScreeningMediaInterface =
      ScreeningMediaDataNormalCalcKL[3];

    return {
      modelName: Object.keys(unitInfoData.models)[1],
      width: Object.values(unitInfoData.models)[1].width,
      length: Object.values(unitInfoData.models)[1].length,
      weight: Object.values(unitInfoData.models)[1].weight,
      motor: Object.values(unitInfoData.models)[1].motor,
      nbrOfDecks: 2,
      deckList: {
        0: {
          separationSize: 25,
          screeningElement: {
            name: Object.values(
              unitInfoData.configurations.screeningMediaIds,
            )[0].name,
            value: Object.values(
              unitInfoData.configurations.screeningMediaIds,
            )[0].value,
          },
          screeningMedia: defaultScreeningMedia,
          openArea: 30,
          particleShape: {
            name: Object.values(unitInfoData.configurations.particleShapes)[0]
              .name,
            value: Object.values(unitInfoData.configurations.particleShapes)[0]
              .value,
          },
          efficiency: {
            name: Object.values(unitInfoData.configurations.efficiencyParams)[1]
              .name,
            value: Object.values(
              unitInfoData.configurations.efficiencyParams,
            )[1].value,
          },
          efficiencyNumber: 80,
          display: defaultDeckDisplay,
        },
        1: {
          separationSize: 16,
          screeningElement: {
            name: Object.values(
              unitInfoData.configurations.screeningMediaIds,
            )[0].name,
            value: Object.values(
              unitInfoData.configurations.screeningMediaIds,
            )[0].value,
          },
          screeningMedia: defaultScreeningMedia,
          openArea: 30,
          particleShape: {
            name: Object.values(unitInfoData.configurations.particleShapes)[0]
              .name,
            value: Object.values(unitInfoData.configurations.particleShapes)[0]
              .value,
          },
          efficiency: {
            name: Object.values(unitInfoData.configurations.efficiencyParams)[1]
              .name,
            value: Object.values(
              unitInfoData.configurations.efficiencyParams,
            )[1].value,
          },
          efficiencyNumber: 80,
          display: defaultDeckDisplay,
        },
      },
      scalpingScreen: false,
      wetScreening: false,
      materialType: {
        name: Object.values(unitInfoData.configurations.rockMaterials)[0].name,
        value: Object.values(unitInfoData.configurations.rockMaterials)[0]
          .value,
      },
      ...BaseUnitConfig.getBaseDefaults(),
      displayLoad: true,
      display: {
        ...BaseUnitConfig.getDefaultDisplay(),
        displayLoad: true,
        displayRiskLevel: false,
        displayNbrOfDecks: false,
        displayWetScreening: false,
      },
    };
  }

  static getNumberOfPorts(): number {
    return 4;
  }
}

export const kwataniScreenConfig = new KwataniScreenConfig();
